import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/kit';
import { useAuthStore } from '@/stores/useAuthStore';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import {
  clock, doctorName, longDayLabel, money, num, todayYmd, ymd,
} from '../lib/scope';
import {
  splitByBranch, useAppointments, useBranchesState, useInvoices, useProviders, useUnreviewedResults,
  useUnsignedNotes,
} from '../lib/useManagerData';

/**
 * Clinic manager · The digest — `g_digest.dc.html`.
 *
 * ITS OWN WINDOW, FULL WIDTH, NO PANE. A message has no rows to select, so
 * there is nothing to dock (Law 4).
 *
 * COUNTS, MONEY AND DOCTOR NAMES — NOTHING ELSE. The board says it under the
 * card, and it is the same wall the home keeps: a digest that names a patient
 * has put PHI in a message that will one day leave on WhatsApp.
 *
 * THE SAME RENDER WHEREVER IT ARRIVES. This is the message, composed from the
 * day it describes, not a page about the message — which is why it is built
 * from the same reads the home uses. What the backend does NOT have is the
 * SENDING: there is no digest job, no schedule and no record of one having gone
 * out, so the card is dated by the day it covers and the "sent 9:02 pm" stamp
 * the board prints is replaced by the truth (read here, not sent). Logged as a
 * BACKEND-GAP.
 */
export function ManagerDigestPage() {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const [day, setDay] = useState(todayYmd());
  const [jump, setJump] = useState('');

  const range = useMemo(() => ({ start: day, end: day }), [day]);
  const invoices = useInvoices(range);
  const appointments = useAppointments(range);
  const { providers } = useProviders();
  const unsigned = useUnsignedNotes();
  const { results } = useUnreviewedResults();
  const { branches, isLoading: branchesLoading } = useBranchesState();
  const clinicName = useAuthStore((st) => st.activeTenant?.displayName?.trim()) || 'This clinic';
  const branchName = branches[0]?.name ?? 'This clinic';
  /* Every figure below is the whole clinic's. With two sites it says so and
     then splits the MONEY by the site that raised each bill — visits are not
     stamped with a site, so they are never split. */
  const manySites = branches.length > 1;
  const subject = manySites ? clinicName : branchName;
  const siteMoney = splitByBranch(invoices.page.rows, branches);
  const loading = invoices.isLoading || appointments.isLoading || branchesLoading;

  const tomorrow = useMemo(() => {
    const [y, m, d] = day.split('-').map(Number);
    const next = new Date(y, m - 1, d + 1);
    return ymd(next);
  }, [day]);
  const tomorrowBookings = useAppointments({ start: tomorrow, end: tomorrow });

  usePageMeta({ title: 'The digest' });

  const visits = appointments.page.rows.filter(
    (a) => a.status !== 'cancelled' && a.status !== 'no-show');
  const collected = invoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const unpaid = invoices.page.rows.reduce((s, i) => s + Number(i.balance || 0), 0);

  const doctorsToday = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of visits) if (a.providerId) counts.set(a.providerId, (counts.get(a.providerId) ?? 0) + 1);
    return [...counts.entries()]
      .map(([id, n]) => ({ id, n, name: doctorName(providers.find((p) => p.id === id)) }))
      .sort((a, b) => b.n - a.n);
  }, [visits, providers]);

  /* THE WARNING LINE IS TWO CLAUSES ON THE BOARD — "2 unsigned notes at Nasr
     City · 1 result unreviewed 3 days". Same sources as the home's banner: the
     unsigned backlog and the lab/imaging stats endpoints' `unreviewed` count.
     No age is recorded anywhere, so "3 days" is left off rather than invented. */
  const warnings: string[] = [];
  if (unsigned.page.total > 0) {
    warnings.push(`${unsigned.page.total} unsigned note${unsigned.page.total === 1 ? '' : 's'} at ${subject}`);
  }
  if (results.recorded && results.unreviewed > 0) {
    warnings.push(`${results.unreviewed} result${results.unreviewed === 1 ? '' : 's'} unreviewed`);
  }
  const anythingWrong = unsigned.page.total > 0 || results.unreviewed > 0;

  const isToday = day === todayYmd();
  const lastBill = useMemo(() => {
    const times = appointments.page.rows
      .filter((a) => invoices.page.rows.some((i) => i.appointmentId === a.id))
      .map((a) => a.time)
      .filter(Boolean)
      .sort();
    return times.length ? clock(times[times.length - 1]) : null;
  }, [appointments.page.rows, invoices.page.rows]);

  const bubble = (
      <Card style={narrow
          ? { borderColor: '#DCD4C4', background: '#fff', maxWidth: 280 }
          : { borderColor: 'var(--beige-500)', background: 'var(--beige-100)', maxWidth: 560 }}>
          <p className="t-sec w6 brand mb4">MAIE</p>
          <p className="t-body" style={{ lineHeight: 1.8 }}>
            {isToday ? 'Today across your clinics:' : `${longDayLabel(day)} across your clinics:`}<br />
            <span className="w6">{subject}</span> — {loading ? '…' : <>{num(visits.length)} visit
            {visits.length === 1 ? '' : 's'} · {money(collected)}</>}
            {!loading && doctorsToday.length ? ` (${doctorsToday.slice(0, 2).map((d) => d.name).join(', ')})` : ''}
            {manySites && !loading ? <><br />{siteMoney.map((b) => `${b.name} ${money(b.collected)}`).join(' · ')}</> : null}
            <br />
            <br />
            {anythingWrong
              ? <>⚠ {warnings.join(' · ')}<br /></>
              : <>✓ nothing unsigned<br /></>}
            💵 {unpaid > 0
              ? `Day closed at ${subject} · unpaid ${num(unpaid)}`
              : 'Day closed with nothing unpaid'}
            <br />
            <br />
            Tomorrow: {subject}{' '}
            {tomorrowBookings.page.total > 0 ? `${tomorrowBookings.page.total} booked` : 'nothing booked'}
          </p>
          <p className="t-cap mut" style={{ textAlign: 'right', marginTop: 8 }}>
            {lastBill ?? '—'}
          </p>
      </Card>
  );

  useShellChrome(
    {
      raw: true,
      /* g_c5 — the phone draws only the WhatsApp thread: the beige wall, the
         one bubble, the line under it. The day jump, Yesterday and the Families
         door live on the desk screen; ‹ leaves. */
      phone: (
        <div className="maie phone-screen" style={{ background: '#E7E0D4' }}>
          <div className="scroll" style={{ background: '#E7E0D4', padding: 16 }}>
            {bubble}
            <p className="t-sec mut" style={{ marginTop: 14, textAlign: 'center' }}>
              Counts and money only — the digest already lives inside the wall.
            </p>
            <p className="t-cap" style={{ marginTop: 18, textAlign: 'center' }}>
              <button type="button" className="rc-link mut" onClick={() => go('/clinic-manager/today')}>‹ Home</button>
            </p>
          </div>
        </div>
      ),
      phoneMenu: (close) => <ManagerMenu door="today" onClose={close} />,
      title: isToday ? 'Today’s digest' : 'The digest',
      sub: `${longDayLabel(day)} · ${lastBill ? `last bill ${lastBill}` : 'nothing billed yet'}`,
      /* THE BOARD'S SEARCH FIELD, and it does what it names: type a date and
          the digest becomes that day's. A box that filters a single message
          would be the dead control §8 forbids. */
      search: 'Search a digest, a date…',
      searchValue: jump,
      onSearchChange: (v) => {
        setJump(v);
        const parsed = parseDay(v);
        if (parsed) setDay(parsed);
      },
      primary: {
        label: 'Yesterday ‹',
        onClick: () => {
          const [y, m, d] = day.split('-').map(Number);
          setDay(ymd(new Date(y, m - 1, d - 1)));
        },
      },
    },
    [day, isToday, lastBill, jump, narrow, branchName, visits.length, collected, unpaid, warnings.join('|'), doctorsToday.map((d) => d.name).join('|'), tomorrowBookings.page.total],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {/* Desk: the board's warm beige panel. Phone: a white message bubble on
            the beige wall of a WhatsApp thread. */}
        <div style={narrow ? { background: '#E7E0D4', borderRadius: 16, padding: 16, marginTop: 8 } : undefined}>
        {bubble}
        </div>

        <p className="t-sec mut" style={{ marginTop: 10, maxWidth: 560 }}>
          Counts and money only — the digest already lives inside the wall.
        </p>

        <p className="t-cap mut" style={{ marginTop: 6, maxWidth: 560 }}>
          It arrives at 9 pm as a notification to every manager; Alerts ▸ Families turns it off.
        </p>

        <p style={{ marginTop: 14 }}>
          <button type="button" className="dchip"
            style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
            /* The label names a SHEET, so it has to open the sheet — the alerts
               screen reads `?families=1` on mount and docks it (BRIEF §4.8). */
            onClick={() => go('/clinic-manager/alerts?families=1')}>
            Open Alerts ▸ Families
          </button>{' '}
          <button type="button" className="dchip"
            style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
            onClick={() => {
              const [y, m, d] = day.split('-').map(Number);
              setDay(ymd(new Date(y, m - 1, d - 1)));
            }}>
            Yesterday’s digest
          </button>
        </p>
      </div>
    </div>
  );
}

/**
 * A typed date, in the shapes a person actually types: `2026-09-06`,
 * `6 Sep 2026`, `6 Sep`. Anything else is not a date and the digest stays put
 * rather than jumping to 1970.
 */
function parseDay(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const withYear = /^\d{1,2}\s+[A-Za-z]{3,}\s+\d{4}$/.test(v) ? v : `${v} ${new Date().getFullYear()}`;
  if (!/^\d{1,2}\s+[A-Za-z]{3,}\s+\d{4}$/.test(withYear)) return null;
  const d = new Date(withYear);
  return Number.isNaN(d.getTime()) ? null : ymd(d);
}
